import * as Xstate from "@xstate/react";
import moment from "moment";
import React, { useCallback, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { HiPlus } from "react-icons/hi2";
import { LuTrash2 } from "react-icons/lu";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Tooltip as ReactTooltip } from "react-tooltip";

import { GlobalContext } from "../../../../../../../../app/stateMachines/GlobalContext";
import { Card } from "../../../../../../../../components/card/Card";
import { LottieLoading } from "../../../../../../../../components/graphics/LottieLoading";
import PreviewFile from "../../../../../../../../components/modal/PreviewFile";
import { SettingsConfirmModal } from "../../../../../../../../components/modal/SettingsConfirmModal";
import { Button } from "../../../../../../../../components/newUI/button";
import ContactFileSvg from "../../../../../../../../components/svg/ContactFileSvg";
import fileIcon from "../../../../../../../../components/svg/file.svg";
import { WarningExclaimSvg } from "../../../../../../../../components/svg/WarningExclaimSvg";
import {
  ContactFileModel,
  WorkFileDocumentModel,
} from "../../../../../../../../factories/contacts/models/contact-file.model";
import { getLinkedFirm } from "../../../../../../../../sessionStorage/sessionStorage";
import { useGetContactQuery } from "../../../../../../../../slices/ContactsSlice";
import {
  useAddContactFileMutation,
  useDeleteContactFileMutation,
  useDeleteWorkFileMutation,
  useGetContactFilesQuery,
  useLazyGetPreviewContactFileQuery,
  useLazyGetPreviewWorkFileQuery,
} from "../../../../../../../../slices/FilesSlice";
import { escapeHtml } from "../../../../../../../../utils/escapeHtml";
import { filterPortalFiles } from "../../../../../../../clientPortal/clientPortal.utils";
import {
  PortalFileTags,
  PortalFilterChips,
  ShareToggleButton,
  usePortalFiles,
} from "../../../../../../../clientPortal/PortalFilesParts";
import { PortalStatusButton } from "../../../../../../../clientPortal/PortalStatusButton";
import { RequestSignatureIcon } from "../../../../../../../signatures/RequestSignatureIcon";
import { InputFile } from "../../../../../../components/InputFile";
import { ProfileWidgetEmptyState } from "../../../../../../components/profile";

type FileListItem = ContactFileModel & {
  fileSource: "contact" | "work";
  workName?: string | null;
  workId?: string | null;
};

const FILE_NAME_TOOLTIP_ID = "contact-files-widget-file-name-tooltip";

const getFileName = (file: FileListItem) =>
  file.originalFileName || file.documentName || "Unknown file";

const getWorkName = (file: WorkFileDocumentModel) =>
  file.work?.name || file.work?.workName || null;

export const ContactFilesWidget = () => {
  const { userInfoService } = React.useContext(GlobalContext);
  const [userInfoState] = Xstate.useActor(userInfoService);
  const { userInfoByEmail } = userInfoState.context;

  const navigate = useNavigate();
  const { customerId } = useParams<{ customerId: string }>();
  const orgId = getLinkedFirm()?.orgId || "";

  const { data: contact } = useGetContactQuery(
    {
      customerId: customerId || "",
      orgId: orgId,
    },
    { skip: !customerId || !orgId },
  );

  const contactId = contact?.contactId || "";

  const {
    data: contactFiles,
    isFetching,
    refetch,
  } = useGetContactFilesQuery(contactId, {
    skip: !contactId,
  });

  const [
    getPreviewContactFile,
    { data: previewContactFile, isLoading: isLoadingContactPreview },
  ] = useLazyGetPreviewContactFileQuery();

  const [
    getPreviewWorkFile,
    { data: previewWorkFile, isLoading: isLoadingWorkPreview },
  ] = useLazyGetPreviewWorkFileQuery();

  const [addFile, { isLoading: isLoadingAdd }] = useAddContactFileMutation();
  const [deleteContactFile, { isLoading: isLoadingDeleteContactFile }] =
    useDeleteContactFileMutation();
  const [deleteWorkFile, { isLoading: isLoadingDeleteWorkFile }] =
    useDeleteWorkFileMutation();

  const addFileRef = useRef<HTMLInputElement>(null);

  const portal = usePortalFiles(
    customerId || "",
    contactFiles?.contactDocuments,
    refetch,
  );

  const [activeFileName, setActiveFileName] = useState("");
  const [activePreviewSource, setActivePreviewSource] = useState<
    "contact" | "work" | null
  >(null);
  const [error, setError] = useState("");
  const [isOpenPreview, setIsOpenPreview] = useState(false);
  const [isOpenErrorPopUp, setIsOpenErrorPopUp] = useState(false);
  // Deleting asks first; deleted files go to the recycle bin (Documents).
  const [fileToDelete, setFileToDelete] = useState<FileListItem | null>(null);

  const filesData = useMemo<FileListItem[]>(() => {
    const contactDocuments: FileListItem[] = (
      contactFiles?.contactDocuments ?? []
    ).map((file) => ({
      ...file,
      fileSource: "contact",
      workName: null,
      workId: null,
    }));

    const workDocuments: FileListItem[] = (
      contactFiles?.workDocuments ?? []
    ).map((file) => ({
      ...file,
      fileSource: "work",
      workName: getWorkName(file),
      workId: file?.work?.id || null,
    }));

    return filterPortalFiles(
      [...contactDocuments, ...workDocuments].sort(
        (a, b) =>
          new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime(),
      ),
      portal.filter,
    );
  }, [contactFiles, portal.filter]);

  const totalFiles =
    (contactFiles?.contactDocuments?.length ?? 0) +
    (contactFiles?.workDocuments?.length ?? 0);

  const previewFileUrl =
    activePreviewSource === "work"
      ? previewWorkFile?.documentUrl
      : previewContactFile?.documentUrl;

  const isLoadingPreview =
    activePreviewSource === "work"
      ? isLoadingWorkPreview
      : isLoadingContactPreview;

  const isLoadingDelete = isLoadingDeleteContactFile || isLoadingDeleteWorkFile;

  const handleFileChange = useCallback(
    (files: File | File[]) => {
      if (!contactId) {
        return;
      }

      const filesArray = Array.isArray(files) ? files : [files];

      void (async () => {
        let failedUploads = 0;

        for (const file of filesArray) {
          const formData = new FormData();

          formData.append("Documents", file);
          formData.append("contactId", contactId);

          if (userInfoByEmail?.id) {
            formData.append("assignedUserRelayId", userInfoByEmail.id);
          }

          formData.append("notes", "");

          try {
            await addFile(formData).unwrap();
          } catch {
            failedUploads += 1;
          }
        }

        if (failedUploads > 0) {
          setError(
            failedUploads === filesArray.length
              ? "Something went wrong"
              : `${failedUploads} file(s) failed to upload`,
          );
        } else {
          setError("");
        }

        if (failedUploads < filesArray.length) {
          void refetch();
        }
      })();
    },
    [addFile, contactId, refetch, userInfoByEmail?.id],
  );

  const handleOpenPreview = useCallback(
    (file: FileListItem) => {
      const fileName = getFileName(file);

      setActiveFileName(fileName);
      setActivePreviewSource(file.fileSource);
      setIsOpenPreview(true);

      if (file.fileSource === "work") {
        void getPreviewWorkFile(file.id);
        return;
      }

      void getPreviewContactFile(file.id);
    },
    [getPreviewContactFile, getPreviewWorkFile],
  );

  const handleDeleteFile = useCallback(
    (file: FileListItem) => {
      void (async () => {
        try {
          if (file.fileSource === "work") {
            await deleteWorkFile(file.id).unwrap();
          } else {
            await deleteContactFile(file.id).unwrap();
          }

          void refetch();
        } catch {
          setError("Something went wrong");
        }
      })();
    },
    [deleteContactFile, deleteWorkFile, refetch],
  );

  return (
    <>
      <Card extraClasses={"overflow-hidden"}>
        <div className={"flex h-[386px] max-w-full flex-col p-[22px]"}>
          <div className={"mb-4 shrink-0"}>
            <div className={"flex w-full items-center justify-between gap-4"}>
              <div className={"flex items-baseline gap-3"}>
                <div className={"text-[19px] font-extrabold text-[#1F2433]"}>
                  Files
                </div>
                <Link
                  to={"../files"}
                  relative={"path"}
                  className={
                    "text-[12.5px] font-bold text-[#6B4EFF] hover:underline"
                  }>
                  See all & folders
                </Link>
              </div>

              <div className={"flex items-center gap-2"}>
                {customerId && <PortalStatusButton customerId={customerId} />}
                <InputFile
                  setFile={handleFileChange}
                  setIsOpenErrorPopUp={setIsOpenErrorPopUp}
                  setError={setError}
                  inputRef={addFileRef}
                  isLoading={isLoadingAdd}
                  multiple
                  button={
                    <Button
                      type={"button"}
                      variant={"outline"}
                      size={"sm"}
                      iconOnly
                      autoWidth
                      leftIcon={<HiPlus size={16} />}
                      loading={isLoadingAdd}
                      className={
                        "h-8 w-8 rounded-lg border-[#E4E7EC] p-0 text-[#6B4EFF] hover:bg-[#F4F2FF]"
                      }
                    />
                  }
                />
              </div>
            </div>

            {error && (
              <div className={"mt-2 text-sm font-semibold text-red-500"}>
                {error}
              </div>
            )}
          </div>

          <div className={"flex min-h-0 flex-1 flex-col"}>
            <PortalFilterChips
              filter={portal.filter}
              setFilter={portal.setFilter}
              counts={portal.counts}
              total={totalFiles}
            />
            {isFetching && !contactFiles ? (
              <div className={"p-4"}>
                <LottieLoading />
              </div>
            ) : !filesData.length ? (
              <ProfileWidgetEmptyState
                icon={<ContactFileSvg />}
                title={"No files"}
                description={
                  "Files uploaded for this profile will appear here."
                }
              />
            ) : (
              <div
                className={
                  "mr-[-22px] min-h-0 flex-1 overflow-y-auto pr-[22px]"
                }>
                {filesData.map((file) => {
                  const fileName = getFileName(file);

                  return (
                    <div
                      key={`${file.fileSource}-${file.id}`}
                      onClick={() => handleOpenPreview(file)}
                      className={
                        "flex cursor-pointer items-center justify-between gap-3 border-b border-[#F4F5F7] py-[13px] transition hover:bg-[#FAFAFC]"
                      }>
                      <div
                        className={
                          "flex min-w-0 flex-1 items-center gap-3 text-[14px]"
                        }>
                        <div className={"h-8 w-8 shrink-0"}>
                          <img src={fileIcon} alt={"file"} />
                        </div>

                        <div className={"min-w-0 flex-1"}>
                          <div
                            data-tooltip-id={FILE_NAME_TOOLTIP_ID}
                            data-tooltip-content={fileName}
                            className={
                              "truncate text-[13.5px] font-bold text-[#1F2433]"
                            }>
                            {fileName}
                          </div>

                          <div className={"mt-0.5 text-xs"}>
                            <div className={"text-[#9097A8]"}>
                              {moment(file.createdDate).format("MMM DD, YYYY")}
                            </div>

                            {file.fileSource === "contact" && (
                              <PortalFileTags
                                file={file}
                                isNew={portal.newIds.has(file.id)}
                              />
                            )}

                            {file.fileSource === "work" && file.workName && (
                              <div
                                onClick={(event) => {
                                  event.stopPropagation();

                                  if (file.workId) {
                                    navigate(`/work/${file.workId}/details`);
                                  }
                                }}
                                data-tooltip-id={FILE_NAME_TOOLTIP_ID}
                                data-tooltip-content={file.workName}
                                className={
                                  "max-w-full truncate text-[#6B4EFF] hover:underline"
                                }>
                                {file.workName}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {file.fileSource === "contact" && customerId && (
                        <ShareToggleButton
                          file={file}
                          disabled={portal.isSharing}
                          onToggle={() => void portal.toggleShare(file)}
                        />
                      )}

                      {file.fileSource === "contact" && customerId && (
                        <RequestSignatureIcon
                          customerId={customerId}
                          fileId={file.id}
                          name={file.originalFileName || file.documentName}
                        />
                      )}

                      <Button
                        type={"button"}
                        variant={"outline"}
                        size={"sm"}
                        iconOnly
                        autoWidth
                        leftIcon={<LuTrash2 size={16} />}
                        disabled={isLoadingDelete}
                        onClick={(event) => {
                          event.stopPropagation();
                          setFileToDelete(file);
                        }}
                        className={
                          "h-8 w-8 shrink-0 rounded-lg border-[#E4E7EC] p-0 text-[#9097A8] hover:bg-[#F1F2F5] disabled:opacity-60"
                        }
                      />
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </Card>

      <ReactTooltip
        id={FILE_NAME_TOOLTIP_ID}
        place={"bottom"}
        className={"!rounded-[8px] !p-2 !text-sm"}
      />

      {isOpenPreview && (
        <PreviewFile
          close={() => {
            setIsOpenPreview(false);
            setActivePreviewSource(null);
          }}
          file={previewFileUrl || ""}
          fileName={activeFileName}
          isLoading={isLoadingPreview}
        />
      )}

      {/* QA batch 15 (PROF-040): over the whole page (a portal), so the
          question is always seen before anything is deleted. */}
      {fileToDelete &&
        createPortal(
          <SettingsConfirmModal
            onClose={() => setFileToDelete(null)}
            onConfirm={() => {
              handleDeleteFile(fileToDelete);
              setFileToDelete(null);
            }}
            icon={<LuTrash2 size={24} />}
            title={"Delete this file?"}
            description={`“${escapeHtml(
              fileToDelete.originalFileName ||
                fileToDelete.documentName ||
                "This file",
            )}” goes to the recycle bin (Documents › Recycle bin). You can restore it for 90 days.`}
            confirmText={"Delete"}
          />,
          document.body,
        )}

      {isOpenErrorPopUp && (
        <SettingsConfirmModal
          noCancel
          confirmText={"Ok"}
          onClose={() => setIsOpenErrorPopUp(false)}
          onConfirm={() => setIsOpenErrorPopUp(false)}
          variant={"warning"}
          confirmButtonStyles={
            "flex w-[200px] ml-[45%] justify-center rounded-lg px-4 py-2 disabled:opacity-75"
          }
          icon={<WarningExclaimSvg sizeCx={"h-6 w-6"} />}
          title={"File Size Exceeds Limit"}
          description={
            "One or more selected file(s) is too large to upload. The maximum allowed size is 50 MB. Please upload a smaller file and try again."
          }
        />
      )}
    </>
  );
};
